import { Box, Button, Flex, Text } from '@chakra-ui/react';
import { NavArrowLeft } from 'iconoir-react';
import { useTranslation } from 'react-i18next';
import { ONBOARDING_PALETTE } from './onboarding-palette';
import { ONBOARDING_STEPS, activeStepIndex, type OnboardingStep } from './onboarding-steps';

interface OnboardingProgressProps {
  step: OnboardingStep;
  /** Shows "Back" when given; the first step has none. */
  onBack?: () => void;
  /** A brand with no online store skips the connection: one step fewer. */
  skipsConnect?: boolean;
}

/**
 * "Back", "Step N of 4" and one bar per step, filled up to the current one.
 * The bars carry the progress for assistive technologies as a progressbar.
 */
export const OnboardingProgress = ({
  step,
  onBack,
  skipsConnect = false,
}: OnboardingProgressProps) => {
  const { t } = useTranslation();
  const current = activeStepIndex(step) + 1;
  const steps = skipsConnect
    ? ONBOARDING_STEPS.filter((key) => key !== 'connect')
    : ONBOARDING_STEPS;
  const total = steps.length;

  return (
    <Box mb="36px">
      <Flex align="center" justify="space-between" minH="24px" mb="12px">
        {onBack ? (
          <Button
            variant="plain"
            size="xs"
            px={0}
            h="auto"
            gap="4px"
            fontSize="13px"
            fontWeight="400"
            color="inkMuted"
            _hover={{ color: 'ink' }}
            onClick={onBack}
          >
            <NavArrowLeft width={16} height={16} aria-hidden />
            {t('common.back')}
          </Button>
        ) : (
          <span />
        )}
        <Text
          fontFamily="heading"
          fontSize="12px"
          fontWeight="600"
          letterSpacing="0.06em"
          color="inkSubtle"
        >
          {t('onboarding.stepCounter', { current, total })}
        </Text>
      </Flex>

      <Flex
        role="progressbar"
        aria-valuemin={1}
        aria-valuemax={total}
        aria-valuenow={current}
        gap="6px"
      >
        {steps.map((key, index) => (
          <Box
            key={key}
            flex="1"
            h="4px"
            borderRadius="full"
            data-state={index < current ? 'filled' : 'empty'}
            bg={index < current ? 'primary.900' : ONBOARDING_PALETTE.progressTrack}
            transition="background-color .3s"
          />
        ))}
      </Flex>
    </Box>
  );
};
